import React, { useState, useEffect, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { supabase } from '../../lib/supabase';
import { formatDateWithEstimate } from '../../utils/dateUtils';
import {
  TrendingUp,
  Target,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Award,
  Zap,
  ChevronRight,
  RefreshCw
} from 'lucide-react';

/**
 * Timeline Progress Widget for Students
 * Provides a comprehensive view of progress across all colleges in their list
 */
const TimelineProgressWidget = () => {
  const { currentUser } = useFamilyContext();
  const { colleges, timeline } = useCollegeContext();
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Load student preferences
  useEffect(() => {
    loadStudentPreferences();
  }, [currentUser?.id]);

  const loadStudentPreferences = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('student_timeline_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading preferences:', error);
      } else if (data) {
        setPreferences(data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate comprehensive progress metrics
  const progressMetrics = useMemo(() => {
    if (!colleges || !timeline) return null;

    const studentColleges = colleges.filter(college => 
      college.user_id === currentUser?.id || 
      college.family_id === currentUser?.family_id
    );

    if (studentColleges.length === 0) return null;

    const now = new Date();
    let totalTasks = 0;
    let completedTasks = 0;
    let overdueTasks = 0;
    let upcomingTasks = 0;
    let urgentTasks = 0;
    const collegeProgress = [];
    
    studentColleges.forEach(college => {
      const collegeTasks = timeline.filter(task => task.college_id === college.id);
      const completed = collegeTasks.filter(task => task.completion_status === 'completed').length;
      const overdue = collegeTasks.filter(task => {
        const dueDate = new Date(task.due_date || task.deadline);
        return dueDate < now && task.completion_status !== 'completed';
      }).length;
      
      const upcoming = collegeTasks.filter(task => {
        const dueDate = new Date(task.due_date || task.deadline);
        const daysUntil = (dueDate - now) / (1000 * 60 * 60 * 24);
        return daysUntil >= 0 && daysUntil <= 14 && task.completion_status !== 'completed';
      }).length;

      const urgent = collegeTasks.filter(task => {
        const dueDate = new Date(task.due_date || task.deadline);
        const daysUntil = (dueDate - now) / (1000 * 60 * 60 * 24);
        return daysUntil >= 0 && daysUntil <= 7 && task.completion_status !== 'completed';
      }).length;

      totalTasks += collegeTasks.length;
      completedTasks += completed;
      overdueTasks += overdue;
      upcomingTasks += upcoming;
      urgentTasks += urgent;

      collegeProgress.push({
        college,
        total: collegeTasks.length,
        completed,
        overdue,
        upcoming,
        urgent,
        completionRate: collegeTasks.length > 0 ? (completed / collegeTasks.length) * 100 : 0,
        nextDeadline: collegeTasks
          .filter(task => task.completion_status !== 'completed')
          .sort((a, b) => new Date(a.due_date || a.deadline) - new Date(b.due_date || b.deadline))[0]
      });
    });

    // Calculate overall metrics
    const overallCompletionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    const overdueRate = totalTasks > 0 ? (overdueTasks / totalTasks) * 100 : 0;
    
    // Determine overall status
    let status = 'on_track';
    let statusColor = 'text-emerald-600';
    let statusBg = 'bg-emerald-50';
    
    if (overdueTasks > 3 || overdueRate > 25) {
      status = 'behind';
      statusColor = 'text-red-600';
      statusBg = 'bg-red-50';
    } else if (overdueTasks > 0 || urgentTasks > 5) {
      status = 'at_risk';
      statusColor = 'text-amber-600';
      statusBg = 'bg-amber-50';
    } else if (overallCompletionRate > 80) {
      status = 'ahead';
      statusColor = 'text-azure-600';
      statusBg = 'bg-azure-50';
    }

    return {
      totalTasks,
      completedTasks,
      overdueTasks,
      upcomingTasks,
      urgentTasks,
      overallCompletionRate,
      overdueRate,
      status,
      statusColor,
      statusBg,
      collegeProgress: collegeProgress.sort((a, b) => b.completionRate - a.completionRate),
      totalColleges: studentColleges.length
    };
  }, [colleges, timeline, currentUser]);

  const getStatusMessage = (status) => {
    switch (status) {
      case 'ahead':
        return 'Great progress! You\'re ahead of schedule';
      case 'on_track':
        return 'You\'re on track with your timeline';
      case 'at_risk':
        return 'Some deadlines need attention soon';
      case 'behind':
        return 'Multiple overdue tasks need immediate attention';
      default:
        return 'Getting started with your college timeline';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ahead':
        return <Award className="w-5 h-5" />;
      case 'on_track':
        return <CheckCircle className="w-5 h-5" />;
      case 'at_risk':
        return <Clock className="w-5 h-5" />;
      case 'behind':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Target className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border-2 border-stone-200 p-4">
        <div className="flex items-center space-x-2 text-stone-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading progress...</span>
        </div>
      </div>
    );
  }

  if (!progressMetrics) {
    return (
      <div className="bg-white rounded-lg border-2 border-stone-200 p-4">
        <div className="flex items-center space-x-2 text-stone-500">
          <BarChart3 className="w-4 h-4" />
          <span className="text-sm">No colleges added yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border-2 border-stone-200 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-stone-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-5 h-5 text-azure-600" />
            <div>
              <h3 className="font-semibold text-slate-900">Timeline Progress</h3>
              <p className="text-xs text-slate-600">
                Tracking {progressMetrics.totalColleges} college{progressMetrics.totalColleges !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${progressMetrics.statusBg} ${progressMetrics.statusColor}`}>
            {getStatusIcon(progressMetrics.status)}
            <span className="uppercase">{progressMetrics.status.replace('_', ' ')}</span>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-4 gap-3 text-center">
          <div>
            <div className="text-lg font-semibold text-slate-900">
              {Math.round(progressMetrics.overallCompletionRate)}%
            </div>
            <div className="text-xs text-slate-600">Complete</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-red-600">
              {progressMetrics.overdueTasks}
            </div>
            <div className="text-xs text-slate-600">Overdue</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-amber-600">
              {progressMetrics.urgentTasks}
            </div>
            <div className="text-xs text-slate-600">Urgent</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-azure-600">
              {progressMetrics.upcomingTasks}
            </div>
            <div className="text-xs text-slate-600">Due Soon</div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            <span>Overall Progress</span>
            <span>{progressMetrics.completedTasks}/{progressMetrics.totalTasks} tasks</span>
          </div>
          <div className="w-full bg-stone-200 rounded-full h-2">
            <div 
              className="bg-azure-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressMetrics.overallCompletionRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-stone-200 p-4 space-y-4">
          
          {/* Status Message */}
          <div className={`p-3 rounded-lg border ${progressMetrics.statusBg} border-opacity-50`}>
            <div className="flex items-start space-x-2">
              <div className={progressMetrics.statusColor}>
                {getStatusIcon(progressMetrics.status)}
              </div>
              <div>
                <p className={`text-sm font-medium ${progressMetrics.statusColor}`}>
                  {getStatusMessage(progressMetrics.status)}
                </p>
                {preferences && (
                  <p className="text-xs text-slate-600 mt-1">
                    Timeline set to {preferences.disclosure_pace} pace • {preferences.workload_capacity} workload
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* College-by-College Breakdown */}
          <div>
            <h4 className="font-medium text-slate-900 text-sm mb-3">College Progress</h4>
            <div className="space-y-3">
              {progressMetrics.collegeProgress.slice(0, expanded ? undefined : 3).map((collegeData) => (
                <div key={collegeData.college.id} className="border border-stone-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <h5 className="font-medium text-slate-900 text-sm">
                        {collegeData.college.name}
                      </h5>
                      <div className="flex items-center space-x-3 text-xs text-slate-600 mt-1">
                        <span>{collegeData.completed}/{collegeData.total} complete</span>
                        {collegeData.overdue > 0 && (
                          <span className="text-red-600">
                            {collegeData.overdue} overdue
                          </span>
                        )}
                        {collegeData.urgent > 0 && (
                          <span className="text-amber-600">
                            {collegeData.urgent} urgent
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">
                        {Math.round(collegeData.completionRate)}%
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-stone-200 rounded-full h-1.5">
                    <div 
                      className="bg-azure-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${collegeData.completionRate}%` }}
                    />
                  </div>
                  
                  {/* Next deadline */}
                  {collegeData.nextDeadline && (
                    <div className="mt-2 text-xs text-stone-500">
                      <span className="font-medium">Next:</span>{' '}
                      {collegeData.nextDeadline.title || collegeData.nextDeadline.task_name} •{' '}
                      {formatDateWithEstimate(collegeData.nextDeadline.due_date || collegeData.nextDeadline.deadline)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Productivity Insights */}
          {preferences && (
            <div>
              <h4 className="font-medium text-slate-900 text-sm mb-2">Your Settings</h4>
              <div className="bg-azure-50 border border-azure-200 rounded-lg p-3 text-sm">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="flex justify-between">
                    <span>Pace:</span>
                    <span className="font-medium capitalize">{preferences.disclosure_pace}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Capacity:</span>
                    <span className="font-medium capitalize">{preferences.workload_capacity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hours/Week:</span>
                    <span className="font-medium">{preferences.available_hours_per_week}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ED Priority:</span>
                    <span className="font-medium">{preferences.early_decision_priority ? '✓' : '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineProgressWidget;